import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getPayrollFields } from "../../network/payrollFieldApi";
import { useAppDispatch } from "../../redux/store";
import { addPayrollFieldValueThunk } from "../../redux/features/payrollField/payrollFieldThunks";
import { fetchPayrollFieldsThunk } from "../../redux/features/payrollField/payrollFieldThunks";
import phpClient from "../../network/phpClient";
import { PayrollField } from "../../types/payrollField";
import { toast } from "react-toastify";
import PersonalInfo from "./components/PersonalInfo";
import PayrollSection from "./components/PayrollSection";

interface FieldValue {
  field_code: string;
  field_value: string;
  effective_date: string;
}

interface EmployeeInfo {
  employee_id: string;
  employee_number: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  profile_image_url: string;
  job_titlessss: string;
  is_active: boolean;
  hire_date: string;
}

export const EmployeeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payrollFields, setPayrollFields] = useState<PayrollField[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, FieldValue>>({});
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 640);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    // default to personal-info on desktop, show list on mobile
    if (isMobile) setActiveSection(null);
    else setActiveSection((prev) => prev || "personal-info");
  }, [isMobile]);

  // Fetch payroll fields on component mount
  useEffect(() => {
    if (id) {
      fetchEmployeeInfo();
      fetchPayrollFields();
    }
  }, [id]);

  const fetchEmployeeInfo = async () => {
    try {
      const response = await phpClient.post<any>({
        url: `/employees/index/${id}`,
        data: {},
      });
      console.log('Employee Info Response:', response);
      if (response.status && response.data && response.data.length > 0) {
        setEmployeeInfo(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching employee info:', err);
    }
  };

  const fetchPayrollFields = async () => {
    try {
      setLoading(true);
      setError(null);
      const resultAction: any = await dispatch(fetchPayrollFieldsThunk(id!));
      if (resultAction && resultAction.payload && resultAction.payload.status) {
        const response = resultAction.payload;
        setPayrollFields(response.data || []);
        const initialValues: Record<string, FieldValue> = {};
        (response.data || []).forEach((field: any) => {
          initialValues[field.field_code] = {
            field_code: field.field_code,
            field_value: field.field_value || "",
            effective_date: field.effective_date || "",
          };
        });
        setFieldValues(initialValues);
      } else {
        const errorMsg = resultAction?.error?.message || "Failed to fetch payroll fields";
        setError(errorMsg);
        console.error("API Error:", errorMsg);
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || "An error occurred while fetching data";
      setError(errorMsg);
      console.error("Error fetching payroll fields:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldCode: string, value: string) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldCode]: {
        ...prev[fieldCode],
        field_value: value,
      },
    }));
  };

  const handleDateChange = (fieldCode: string, dateStr: string | null) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldCode]: {
        ...prev[fieldCode],
        effective_date: dateStr || "",
      },
    }));
  };

  const handleSaveField = async (field: PayrollField) => {
    const fieldValue = fieldValues[field.field_code];

    if (!fieldValue.field_value) {
      toast.error("Please enter a value");
      return;
    }

    if (!fieldValue.effective_date) {
      toast.error("Please select an effective date");
      return;
    }

    try {
      setSavingField(field.field_code);
      const resultAction: any = await dispatch(
        addPayrollFieldValueThunk({
          employee_id: id!,
          field_code: field.field_code,
          field_value: fieldValue.field_value,
          effective_date: fieldValue.effective_date,
        })
      );

      if (resultAction && resultAction.payload && resultAction.payload.status) {
        toast.success(resultAction.payload.message || "Field saved successfully");
        await fetchPayrollFields();
      } else {
        const errMsg = resultAction.error?.message || "Failed to save field";
        toast.error(errMsg);
        console.error("Save Error:", errMsg);
      }
    } catch (err: any) {
      const errorMsg = err?.message || "An error occurred while saving";
      toast.error(errorMsg);
      console.error("Error saving field:", err);
    } finally {
      setSavingField(null);
    }
  };

  const handleSaveBatch = async (changes: Record<string, { field_value?: string; effective_date?: string }>) => {
    const keys = Object.keys(changes);
    if (keys.length === 0) return;

    let successCount = 0;
    const errors: string[] = [];

    for (const code of keys) {
      const change = changes[code] || {};
      const existing = fieldValues[code] || {};

      const field_value = change.field_value ?? existing.field_value;
      const effective_date = change.effective_date ?? existing.effective_date;

      if (!field_value) {
        errors.push(`${code}: missing value`);
        continue;
      }
      if (!effective_date) {
        errors.push(`${code}: missing effective date`);
        continue;
      }

      try {
      try {
        const resultAction: any = await dispatch(
          addPayrollFieldValueThunk({
            employee_id: id!,
            field_code: code,
            field_value,
            effective_date,
          })
        );

        if (resultAction && resultAction.payload && resultAction.payload.status) {
          successCount += 1;
        } else {
          errors.push(`${code}: ${resultAction.error?.message || 'failed'}`);
        }
      } catch (err: any) {
        errors.push(`${code}: ${err?.message || 'error'}`);
      }
      } catch (err: any) {
        errors.push(`${code}: ${err?.message || 'error'}`);
      }
    }

    if (successCount > 0) {
      toast.success("Payroll Field value saved successfully");
      await fetchPayrollFields();
    }

    if (errors.length > 0) {
      toast.error(`Some changes failed: ${errors.join('; ')}`);
    }
  };

  // renderFieldInput moved into tailwind components

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto">
        {/* Header removed per design */}

        {/* Error Alert */}
        {error && (
          <div className="mb-4 border border-red-200 bg-red-50 p-3 rounded">
            <div className="text-sm text-red-800">{error}</div>
            <div className="text-xs text-red-600 mt-1">Check browser console for more details. Ensure the API endpoint is correct and the employee ID exists.</div>
          </div>
        )}

        {/* Responsive: on mobile show per-category pages, on web show inline sections */}
        <div className="mb-6 bg-white shadow rounded">
          <div className="p-4">
            {(() => {
              const sections = [
                { key: "personal-info", label: "Personal Info" },
                { key: "job-details", label: "Job Details" },
                { key: "payroll", label: "Payroll" },
                { key: "attendance", label: "Attendance" },
                { key: "access-roles", label: "Access & Roles" },
                { key: "custom-fields", label: "Custom Fields" },
              ];

              if (isMobile) {
                if (activeSection) {
                  return (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <button onClick={() => setActiveSection(null)} className="flex items-center gap-2 text-sm text-blue-600">
                          ← Back
                        </button>
                        <h3 className="text-black font-bold text-lg">{sections.find((s) => s.key === activeSection)?.label}</h3>
                      </div>
                      <div>
                        {activeSection === "personal-info" && <PersonalInfo employeeInfo={employeeInfo} />}
                        {activeSection === "payroll" && <PayrollSection />}
                      </div>
                    </div>
                  );
                }

                return (
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <button onClick={() => navigate(-1)} className="p-0 text-gray-700">←</button>
                      <h3 className="text-black font-bold text-lg">Employee Information</h3>
                    </div>
                    <ul className="divide-y">
                      {sections.map((s) => (
                        <li key={s.key}>
                          <button
                            onClick={() => setActiveSection(s.key)}
                            className={`w-full text-left py-2 text-sm ${activeSection === s.key ? 'font-semibold text-blue-600 bg-[#f6f7fa] rounded pl-4 pr-3 py-2 border-l-4 border-blue-400 shadow-sm' : 'text-gray-800 px-3 py-2'}`}
                          >
                            {s.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }

              // Desktop: show left sidebar with sections and right pane with selected content
              return (
                <div className="md:flex md:gap-2">
                  <aside className="md:w-1/4 p-1">
                      <ul>
                        {sections.map((s) => (
                          <li key={s.key}>
                            <button
                              onClick={() => setActiveSection(s.key)}
                              className={`w-full text-left py-2 px-2 text-sm ${activeSection === s.key ? 'font-semibold text-blue-600 bg-[#f6f7fa] rounded pl-4 pr-3 py-2 border-l-4 border-blue-400 shadow-sm' : 'text-gray-800 px-3 py-2'}`}
                            >
                              {s.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                  </aside>
                  <div className="hidden md:block w-px bg-gray-200 mx-1" />
                  <main className="md:flex-1 p-0">
                    {activeSection === "personal-info" && <PersonalInfo employeeInfo={employeeInfo} />}
                    {activeSection === "payroll" && <PayrollSection />}
                  </main>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
  );
};

export default EmployeeEdit;